import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import type { ChatClient } from "../lib/chatApi";
import { formatOptionLabel } from "../lib/filterPresentation";
import type {
  AvailableFilters,
  ChatCandidates,
  FilterOption,
  SimpleSearchFilters,
  SimpleSearchSortKey,
} from "../lib/types";
import type { Product } from "../lib/mockData";
import { mapRecommendedToProduct } from "../lib/utils";

interface SimpleSearchContextValue {
  products: Product[];
  queryTitle: string;
  resultsCount: number;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  availableFilters: AvailableFilters;
  activeFilters: SimpleSearchFilters;
  activeSort: SimpleSearchSortKey;
  search: (query: string) => Promise<void>;
  fetchMore: () => Promise<void>;
  toggleFilter: (key: string, value: string) => void;
  setRangeFilter: (key: string, min?: number, max?: number) => void;
  setSort: (sort: SimpleSearchSortKey) => void;
  onProductClick: () => void;
}

const SimpleSearchContext = createContext<SimpleSearchContextValue | undefined>(undefined);

const DEFAULT_SORT: SimpleSearchSortKey = "relevance";
const HIDDEN_FILTER_KEYS = new Set(["color"]);

function sanitizeFiltersForRequest(filters: SimpleSearchFilters): SimpleSearchFilters {
  const next: SimpleSearchFilters = {};
  for (const [key, value] of Object.entries(filters)) {
    if (HIDDEN_FILTER_KEYS.has(key)) continue;
    next[key] = value;
  }
  return next;
}

function parseAvailableFilters(raw: unknown): AvailableFilters {
  if (!raw || typeof raw !== "object") return {};
  const result: AvailableFilters = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (HIDDEN_FILTER_KEYS.has(key)) continue;
    if (!value || typeof value !== "object") continue;
    const v = value as Record<string, unknown>;
    if (v.type === "range") {
      result[key] = {
        type: "range",
        min: typeof v.min === "number" ? v.min : null,
        max: typeof v.max === "number" ? v.max : null,
      };
    } else if (v.type === "multi_select" && Array.isArray(v.values)) {
      result[key] = {
        type: "multi_select",
        values: (v.values as unknown[])
          .map((item): FilterOption | null => {
            if (typeof item === "string") return { key: item, label: formatOptionLabel(item) };
            if (item && typeof item === "object" && typeof (item as any).key === "string") {
              const obj = item as Record<string, unknown>;
              return { key: obj.key as string, label: formatOptionLabel(typeof obj.label === "string" ? obj.label : obj.key as string) };
            }
            return null;
          })
          .filter((x): x is FilterOption => x !== null),
      };
    }
  }
  return result;
}

function resolveLatestTestSessionId(explicitTestSessionId?: string): string | undefined {
  const explicit = (explicitTestSessionId || "").trim();
  if (explicit) return explicit;
  try {
    const stored = (localStorage.getItem("ww:test_session_id") || "").trim();
    if (stored) return stored;
  } catch {
    // Ignore storage errors
  }
  const globalId = ((window as any).__WW__?.testSessionId || "").trim();
  return globalId || undefined;
}

function useProvideSimpleSearch(client: ChatClient, minimal?: boolean, testSessionId?: string): SimpleSearchContextValue {
  const [products, setProducts] = useState<Product[]>([]);
  const [queryTitle, setQueryTitle] = useState("");
  const [queryText, setQueryText] = useState("");
  const [resultsCount, setResultsCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchId, setSearchId] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [availableFilters, setAvailableFilters] = useState<AvailableFilters>({});
  const [activeFilters, setActiveFilters] = useState<SimpleSearchFilters>({});
  const [activeSort, setActiveSort] = useState<SimpleSearchSortKey>(DEFAULT_SORT);

  // Refs to avoid stale closures in rapid interactions
  const activeFiltersRef = useRef<SimpleSearchFilters>({});
  const activeSortRef = useRef<SimpleSearchSortKey>(DEFAULT_SORT);
  const refinementAbortRef = useRef<AbortController | null>(null);

  const applyFiltersAndSort = useCallback(async (filters: SimpleSearchFilters, sort: SimpleSearchSortKey) => {
    if (!searchId) return;

    refinementAbortRef.current?.abort();
    const controller = new AbortController();
    refinementAbortRef.current = controller;
    const requestFilters = sanitizeFiltersForRequest(filters);

    setLoading(true);
    setError(null);
    setProducts([]);

    try {
      const res = await client.postCandidatesPage({
        session_id: sessionId || "",
        search_id: searchId,
        offset: 0,
        limit: 24,
        sort_by: sort,
        filters: Object.keys(requestFilters).length > 0 ? requestFilters : undefined,
      }, controller.signal);

      if (controller.signal.aborted) return;
      setProducts(res.candidates.map((r) => mapRecommendedToProduct(r, searchId)));
      setOffset(res.offset + res.candidates.length);
      setHasMore(res.has_more);
      setResultsCount(res.total);
      if (res.available_filters) {
        setAvailableFilters(parseAvailableFilters(res.available_filters));
      }
    } catch (err) {
      if ((err as any)?.name === "AbortError") return;
      if (!controller.signal.aborted) {
        setError((err as Error)?.message || "Failed to apply filters");
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [client, searchId, sessionId]);

  const search = useCallback(async (query: string) => {
    const trimmed = (query || "").trim();
    if (!trimmed) return;

    refinementAbortRef.current?.abort();
    activeFiltersRef.current = {};
    activeSortRef.current = DEFAULT_SORT;
    setActiveFilters({});
    setActiveSort(DEFAULT_SORT);
    setAvailableFilters({});
    setLoading(true);
    setError(null);
    setQueryTitle(trimmed);
    setQueryText(trimmed);
    setProducts([]);
    setSearchId(null);
    setSessionId(null);
    setResultsCount(0);
    setOffset(0);
    setHasMore(false);

    try {
      await client.postChat(null, trimmed, undefined, undefined, {
        route: "NEW_SEARCH",
        minimal: minimal === true,
        sort: DEFAULT_SORT,
        testSessionId: resolveLatestTestSessionId(testSessionId),
        onCandidates: (c: ChatCandidates) => {
          try { performance.mark("candidates-received"); } catch {}
          const sid = c.search_id || null;
          const count = typeof (c as any).results_count === "number"
            ? (c as any).results_count
            : c.candidates.length;

          setSearchId(sid);
          setSessionId((c as any).session_id || null);
          setResultsCount(count);
          setProducts(c.candidates.map((r) => mapRecommendedToProduct(r, sid || undefined)));
          setOffset(c.candidates.length);
          setHasMore(c.candidates.length < count);
          setAvailableFilters(parseAvailableFilters(c.available_filters));
        },
      });
    } catch (err) {
      setError((err as Error)?.message || "Search failed");
    } finally {
      setLoading(false);
    }
  }, [client, minimal, testSessionId]);

  const fetchMore = useCallback(async () => {
    if (!searchId || loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const requestFilters = sanitizeFiltersForRequest(activeFiltersRef.current);
      const res = await client.postCandidatesPage({
        session_id: sessionId || "",
        search_id: searchId,
        offset,
        limit: 24,
        sort_by: activeSortRef.current,
        filters: Object.keys(requestFilters).length > 0 ? requestFilters : undefined,
      });

      setProducts((prev) => [
        ...prev,
        ...res.candidates.map((r) => mapRecommendedToProduct(r, searchId)),
      ]);
      setOffset(res.offset + res.candidates.length);
      setHasMore(res.has_more);
      if (res.available_filters) {
        setAvailableFilters(parseAvailableFilters(res.available_filters));
      }
    } catch (err) {
      setError((err as Error)?.message || "Failed to load more");
    } finally {
      setLoadingMore(false);
    }
  }, [client, searchId, sessionId, offset, loadingMore, hasMore]);

  const onProductClick = useCallback(() => {
    if (!sessionId) return;
    try { localStorage.setItem("pako_session_id", String(sessionId)); } catch {}
    client.postProductClick(sessionId).catch(() => {});
  }, [client, sessionId]);

  const toggleFilter = useCallback((key: string, value: string) => {
    const prev = activeFiltersRef.current;
    const existing = prev[key];
    const arr = Array.isArray(existing) ? existing : [];
    const nextArr = arr.includes(value)
      ? arr.filter((v) => v !== value)
      : [...arr, value];
    const next = { ...prev };
    if (nextArr.length === 0) delete next[key];
    else next[key] = nextArr;
    activeFiltersRef.current = next;
    setActiveFilters(next);
    applyFiltersAndSort(next, activeSortRef.current);
  }, [applyFiltersAndSort]);

  const setRangeFilter = useCallback((key: string, min?: number, max?: number) => {
    const next = { ...activeFiltersRef.current };
    if (min === undefined && max === undefined) {
      delete next[key];
    } else {
      next[key] = {
        ...(min !== undefined ? { min } : {}),
        ...(max !== undefined ? { max } : {}),
      };
    }
    activeFiltersRef.current = next;
    setActiveFilters(next);
    applyFiltersAndSort(next, activeSortRef.current);
  }, [applyFiltersAndSort]);

  const setSort = useCallback((sort: SimpleSearchSortKey) => {
    activeSortRef.current = sort;
    setActiveSort(sort);
    applyFiltersAndSort(activeFiltersRef.current, sort);
  }, [applyFiltersAndSort]);

  return {
    products,
    queryTitle,
    resultsCount,
    loading,
    loadingMore,
    error,
    hasMore,
    availableFilters,
    activeFilters,
    activeSort,
    search,
    fetchMore,
    toggleFilter,
    setRangeFilter,
    setSort,
    onProductClick,
  };
}

interface SimpleSearchProviderProps {
  children: React.ReactNode;
  client: ChatClient;
  minimal?: boolean;
  testSessionId?: string;
}

export function SimpleSearchProvider({ children, client, minimal, testSessionId }: SimpleSearchProviderProps) {
  const value = useProvideSimpleSearch(client, minimal, testSessionId);
  return (
    <SimpleSearchContext.Provider value={value}>
      {children}
    </SimpleSearchContext.Provider>
  );
}

export function useSimpleSearch(): SimpleSearchContextValue {
  const ctx = useContext(SimpleSearchContext);
  if (!ctx) {
    throw new Error("useSimpleSearch must be used within a SimpleSearchProvider");
  }
  return ctx;
}
